import React, { useEffect } from 'react';
import { Control, Controller, useFormContext, useWatch } from 'react-hook-form';
import {
  GeneralSelect,
  OptionType,
} from 'v2/form/CustomSelectors/GeneralSelect';
import { UserInviteTeamMemberForm } from '../../InviteTeamMemberModal';
import styles from './InviteTeamMemberFormFields.module.scss';
import { components } from 'react-select';
import EmailsField from './fields/EmailsField';
import { MAX_EMAIL_INVITATIONS_PER_ORGANIZATION } from 'utils/constants';
import { Organization } from 'v2/models';
import AlertError from 'v2/common/alerts/AlertError/AlertError';
import {
  useStrapiUserEmailValidation,
  useUserInviteEmailValidation,
} from 'v2/queries';

export enum EmailInputTextHelp {
  ReachedLimit = 'You have reached the maximum number of team members you are allowed to invite at a time.',
  ReturnAfterTyping = 'Press Return after typing each email address.',
}

type InviteTeamMemberFormFieldsProps = {
  organizations: Organization[];
  showOrganizationDropdown: boolean;
  control: Control<UserInviteTeamMemberForm>;
  index: number;
  selectedOption?: OptionType;
};

const InviteTeamMemberFormFields = ({
  organizations,
  showOrganizationDropdown,
  control,
  index,
  selectedOption,
}: InviteTeamMemberFormFieldsProps) => {
  const bulkInvitations = useWatch({ control, name: 'bulkInvitations' });
  const invalidEmails = useWatch({ control, name: 'invalidEmails' });
  const { setValue } = useFormContext<UserInviteTeamMemberForm>();
  const allEnteredEmails = bulkInvitations.flatMap(
    (bulkInvitation) => bulkInvitation.inputEmails.emails
  );
  const fieldEnteredEmails = bulkInvitations[index].inputEmails.emails;
  const lastEnteredEmail: string | undefined =
    fieldEnteredEmails[fieldEnteredEmails.length - 1];

  const {
    mutate: checkUserInviteEmail,
    error: userInviteEmailValidationError,
    reset: resetUserInviteEmailValidation,
  } = useUserInviteEmailValidation();

  const {
    mutate: checkStrapiUserEmail,
    error: strapiUserEmailValidationError,
    reset: resetStrapiUserEmailValidation,
  } = useStrapiUserEmailValidation();

  useEffect(() => {
    resetStrapiUserEmailValidation();
    resetUserInviteEmailValidation();

    if (lastEnteredEmail) {
      checkStrapiUserEmail(lastEnteredEmail);
      checkUserInviteEmail(lastEnteredEmail);
    }
  }, [lastEnteredEmail]);

  const error =
    strapiUserEmailValidationError ?? userInviteEmailValidationError ?? '';

  useEffect(() => {
    if (error && !invalidEmails.includes(lastEnteredEmail))
      setValue('invalidEmails', [...invalidEmails, lastEnteredEmail], {
        shouldValidate: true,
      });
  }, [error]);
  return (
    <div className={styles.inputs}>
      {showOrganizationDropdown && (
        <Controller
          control={control}
          name={`bulkInvitations.${index}.organization`}
          render={({ field }) => (
            <GeneralSelect
              aria-label="Organization"
              selectedOption={selectedOption}
              options={organizations.map(({ id, name }) => ({
                label: name,
                value: id,
              }))}
              handleChange={(option) => option && field.onChange(option.value)}
              components={{
                DropdownIndicator:
                  organizations.length === 0
                    ? null
                    : components.DropdownIndicator,
              }}
              menuIsOpen={organizations.length === 0 ? false : undefined}
              fullWidth
            />
          )}
        />
      )}

      <Controller
        control={control}
        name={`bulkInvitations.${index}.inputEmails`}
        rules={{
          validate: (inputEmails) =>
            inputEmails.emails.every(
              (email) => !invalidEmails.includes(email)
            ) && inputEmails.inputValue === '',
        }}
        render={({
          field: {
            onChange,
            value: { emails, inputValue },
          },
        }) => {
          return (
            <>
              <EmailsField
                onChange={onChange}
                emails={emails}
                inputValue={inputValue}
                emailsToIgnore={allEnteredEmails}
                invalidEmails={invalidEmails}
              />

              <p className="text-primary-dark opacity-05 m-0">
                {(emails.length === MAX_EMAIL_INVITATIONS_PER_ORGANIZATION &&
                  EmailInputTextHelp.ReachedLimit) ||
                  EmailInputTextHelp.ReturnAfterTyping}
              </p>

              {error && <AlertError message={error} />}
            </>
          );
        }}
      />
    </div>
  );
};

export default InviteTeamMemberFormFields;
